import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  PeopleContainer,
  EmployeeDirectoryContainer,
  TopContainer,
  MainContentPeople,
  AnimatedTabContent,
} from './People.styles';
import { EmployeeDirectory } from '../../components/People';
import { TabsMenu } from '../../components';
import AdminDrawer from '../../components/AdminDrawer/AdminDrawer';
import { DRAWER_CONFIG } from './peopleDrawerConfig';
import { useDrawer } from '../../utils/Hooks/drawer/useDrawer';
import Tips from '../../components/Tips/Tips';
import { fetchEmployees } from '../../redux/actions/employeesActions';
import { selectHasPrivilegedRole, selectCurrentVenue } from '../../redux/slices/currentUserSlice';

const People = () => {
  const dispatch = useDispatch();
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);
  const currentVenue = useSelector(selectCurrentVenue);
  const [selectedTab, setSelectedTab] = useState(0);

  // Check if Tips feature is enabled for this venue
  const isTipsFeatureEnabled = currentVenue?.isTipsFeatureEnabled ?? false;

  // Build tabs menu options based on user privileges and feature flags
  const shouldShowTipsTab = hasPrivilegedRole && isTipsFeatureEnabled;
  const tabsMenuOptions = shouldShowTipsTab ? ['General', 'Tips'] : ['General'];
  const shouldShowTabsMenu = tabsMenuOptions.length > 1;

  // Fetch employees once when component mounts
  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Reset to General tab if user loses privileged access or Tips feature is disabled while on Tips tab
  useEffect(() => {
    if (!shouldShowTipsTab && selectedTab === 1) {
      setSelectedTab(0);
    }
  }, [shouldShowTipsTab, selectedTab]);

  const {
    isDrawerOpen,
    drawerType,
    drawerTitle,
    handleOpenDrawer,
    handleCloseDrawer,
    handleBackNavigation,
    renderDrawerContent,
  } = useDrawer(
    {
      DRAWER_CONFIG,
    },
    null
  );

  const navigate = useNavigate();

  const handleCloseAction = () => {
    handleCloseDrawer();
    navigate('/people');
  };

  const handleTabChange = (newValue) => {
    setSelectedTab(newValue);
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0: // General
        return <EmployeeDirectory handleOpenDrawer={handleOpenDrawer} />;
      case 1: // Tips
        // Defensive check: only render Tips if user has privileged role and feature is enabled
        if (shouldShowTipsTab) {
          return <Tips handleOpenDrawer={handleOpenDrawer} />;
        }
        return <EmployeeDirectory handleOpenDrawer={handleOpenDrawer} />;
      default:
        return null;
    }
  };

  return (
    <PeopleContainer>
      <EmployeeDirectoryContainer className={isDrawerOpen ? 'drawer-open' : ''}>
        {shouldShowTabsMenu && (
          <TopContainer>
            <TabsMenu options={tabsMenuOptions} value={selectedTab} onChange={handleTabChange} />
          </TopContainer>
        )}
        <MainContentPeople>
          <AnimatedTabContent key={selectedTab}>{renderTabContent()}</AnimatedTabContent>
        </MainContentPeople>
      </EmployeeDirectoryContainer>
      {isDrawerOpen && (
        <AdminDrawer
          title={drawerTitle}
          isOpen={isDrawerOpen}
          onClose={handleCloseAction}
          onBack={DRAWER_CONFIG[drawerType]?.parent ? handleBackNavigation : undefined}
        >
          {renderDrawerContent()}
        </AdminDrawer>
      )}
    </PeopleContainer>
  );
};

export default People;
